/**
 * Chutt... - Super Admin Dashboard (Refonte)
 * 3 tabs: Clients (expandable agencies+users), Couts API (grouped by provider), Tarification.
 * KPI bar always visible at top.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Collapse,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  InputAdornment,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockResetIcon from '@mui/icons-material/LockReset';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import Tooltip from '@mui/material/Tooltip';

import GlassCard from '../components/shared/GlassCard';
import ScrollReveal from '../components/shared/ScrollReveal';
import SAKPIGrid from '../components/superadmin/SAKPIGrid';
import SAAgencyList from '../components/superadmin/SAAgencyList';
import SACharts from '../components/superadmin/SACharts';
import SAFooterCards from '../components/superadmin/SAFooterCards';
import { superadminAPI as SUPERADMIN_API } from '../services/api';
import { colors } from '../styles/colors';

// ─── Plan badge colors ─────────────────────────────────────────
const PLAN_CHIP = {
  demonstration: { bg: colors.gray200, color: colors.textMed },
  production: { bg: colors.successBg, color: colors.successDark },
};

function getPlanChipStyle(plan) {
  const key = (plan || 'production').toLowerCase();
  return PLAN_CHIP[key] || PLAN_CHIP.production;
}

// ─── Provider display names + colors ──────────────────────────
const PROVIDER_META = {
  anthropic: { label: 'Claude (Anthropic)', color: colors.warningDark, bg: colors.warningBg },
  perplexity: { label: 'Perplexity', color: colors.infoDark, bg: colors.infoBg },
  apify: { label: 'Apify', color: colors.successDark, bg: colors.successBg },
  twitter: { label: 'Twitter / X', color: colors.extended.bleu, bg: 'rgba(43, 76, 126, 0.1)' },
  serper: { label: 'Serper (SERP)', color: colors.extended.orange, bg: 'rgba(232, 93, 59, 0.1)' },
  google_cse: { label: 'Google CSE', color: colors.extended.ocre, bg: 'rgba(212, 168, 67, 0.1)' },
  email: { label: 'Email', color: colors.extended.violet, bg: 'rgba(124, 58, 237, 0.1)' },
};

function getProviderMeta(provider) {
  return PROVIDER_META[provider] || { label: provider, color: colors.textMed, bg: colors.gray100 };
}

// ─── Tab Panel helper ──────────────────────────────────────────
function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ─── Styled table cell overrides ───────────────────────────────
const headCellSx = {
  fontFamily: 'DM Sans, sans-serif',
  fontWeight: 600,
  fontSize: '0.72rem',
  color: colors.textMed,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: `1px solid ${colors.border}`,
  py: 1.2,
};

const bodyCellSx = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.84rem',
  color: colors.text,
  borderBottom: `1px solid ${colors.borderLight}`,
  py: 1.2,
};

// ═══════════════════════════════════════════════════════════════
// Expandable Agency Row
// ═══════════════════════════════════════════════════════════════
function AgencyRow({ agency, onToggleAgency, onToggleUser, onResetPassword, onToggleSuperadmin, onCreateUser, onEditAgency }) {
  const [open, setOpen] = useState(false);
  const planStyle = getPlanChipStyle(agency.plan);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: colors.emeraldGlass } }}
        onClick={() => setOpen(!open)}
      >
        <TableCell sx={bodyCellSx} width={30}>
          <IconButton size="small" aria-label={open ? 'Replier' : 'Developper'} sx={{ color: colors.textMed }}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ fontSize: 18, color: colors.primary }} />
            <Typography sx={{ fontWeight: 600, fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem' }}>
              {agency.name}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <Chip
            label={(agency.plan || 'production').toUpperCase()}
            size="small"
            sx={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', fontWeight: 600,
              bgcolor: planStyle.bg, color: planStyle.color, borderRadius: '100px',
            }}
          />
        </TableCell>
        <TableCell sx={bodyCellSx} align="center">{agency.brand_count ?? '--'}</TableCell>
        <TableCell sx={bodyCellSx} align="center">{agency.user_count ?? '--'}</TableCell>
        <TableCell sx={bodyCellSx} align="center">
          <Chip
            label={agency.is_active ? 'Actif' : 'Inactif'}
            size="small"
            sx={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', fontWeight: 600,
              bgcolor: agency.is_active ? colors.successBg : colors.negativeBg,
              color: agency.is_active ? colors.successDark : colors.dangerDark,
              borderRadius: '100px',
            }}
          />
        </TableCell>
        <TableCell sx={bodyCellSx} align="right">
          {agency.monthly_billing
            ? `${Number(agency.monthly_billing).toLocaleString('fr-FR')} \u20AC`
            : '--'}
        </TableCell>
        <TableCell sx={bodyCellSx}>
          {agency.last_invoice_date
            ? new Date(agency.last_invoice_date).toLocaleDateString('fr-FR')
            : '--'}
        </TableCell>
        <TableCell sx={bodyCellSx}>{agency.contact_email || '--'}</TableCell>
        <TableCell sx={bodyCellSx} align="right">
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEditAgency(agency); }}
              title="Editer agence"
              aria-label="Editer agence"
              sx={{ color: colors.primary }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onToggleAgency(agency); }}
              title={agency.is_active ? 'Desactiver' : 'Activer'}
              aria-label={agency.is_active ? 'Desactiver agence' : 'Activer agence'}
              sx={{ color: agency.is_active ? colors.danger : colors.success }}
            >
              {agency.is_active ? <BlockIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expanded content */}
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? `1px solid ${colors.borderLight}` : 'none' }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 2 }}>
              {/* Users sub-table */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.82rem', color: colors.charcoal }}>
                  Utilisateurs ({(agency.users || []).length})
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => onCreateUser(agency.id)}
                  sx={{
                    borderRadius: '100px', fontFamily: 'DM Sans, sans-serif', textTransform: 'none',
                    fontSize: '0.75rem', borderColor: colors.primary, color: colors.primary,
                    '&:hover': { borderColor: colors.primaryDark, bgcolor: colors.primaryBg },
                  }}
                >
                  Creer un utilisateur
                </Button>
              </Box>

              {(agency.users || []).length > 0 ? (
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headCellSx, fontSize: '0.68rem' }}>Email</TableCell>
                        <TableCell sx={{ ...headCellSx, fontSize: '0.68rem' }}>Mot de passe</TableCell>
                        <TableCell sx={{ ...headCellSx, fontSize: '0.68rem' }}>Nom</TableCell>
                        <TableCell sx={{ ...headCellSx, fontSize: '0.68rem' }}>Role</TableCell>
                        <TableCell sx={{ ...headCellSx, fontSize: '0.68rem' }}>Dernier login</TableCell>
                        <TableCell sx={{ ...headCellSx, fontSize: '0.68rem' }} align="center">Actif</TableCell>
                        <TableCell sx={{ ...headCellSx, fontSize: '0.68rem' }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(agency.users || []).map((user) => (
                        <TableRow key={user.id} sx={{ '&:hover': { bgcolor: colors.emeraldGlass } }}>
                          <TableCell sx={{ ...bodyCellSx, fontSize: '0.8rem' }}>{user.email}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontSize: '0.8rem', fontFamily: 'monospace', letterSpacing: '0.05em' }}>{user.initial_password || '--'}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontSize: '0.8rem' }}>{user.full_name || '--'}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontSize: '0.8rem' }}>
                            <Chip
                              label={user.role || 'viewer'}
                              size="small"
                              sx={{
                                fontFamily: 'DM Sans, sans-serif', fontSize: '0.65rem', fontWeight: 600,
                                bgcolor: colors.primaryBg, color: colors.primary, borderRadius: '100px',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontSize: '0.8rem' }}>
                            {user.last_login
                              ? new Date(user.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : '--'}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontSize: '0.8rem' }} align="center">
                            <Chip
                              label={user.is_active ? 'Actif' : 'Inactif'}
                              size="small"
                              sx={{
                                fontFamily: 'DM Sans, sans-serif', fontSize: '0.65rem', fontWeight: 600,
                                bgcolor: user.is_active ? colors.successBg : colors.negativeBg,
                                color: user.is_active ? colors.successDark : colors.dangerDark,
                                borderRadius: '100px',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontSize: '0.8rem' }} align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <IconButton
                                size="small"
                                onClick={() => onToggleUser(user, 'active')}
                                title={user.is_active ? 'Desactiver' : 'Activer'}
                                aria-label={user.is_active ? 'Desactiver utilisateur' : 'Activer utilisateur'}
                                sx={{ color: user.is_active ? colors.danger : colors.success }}
                              >
                                {user.is_active ? <BlockIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => onToggleSuperadmin(user)}
                                title={user.is_superadmin ? 'Retirer superadmin' : 'Promouvoir superadmin'}
                                aria-label={user.is_superadmin ? 'Retirer superadmin' : 'Promouvoir superadmin'}
                                sx={{ color: user.is_superadmin ? colors.warning : colors.textLight }}
                              >
                                <AdminPanelSettingsIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => onResetPassword(user.id)}
                                title="Reinitialiser mot de passe"
                                aria-label="Reinitialiser mot de passe"
                                sx={{ color: colors.info }}
                              >
                                <LockResetIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: colors.textLight, mb: 2 }}>
                  Aucun utilisateur
                </Typography>
              )}

              {/* Brands chips */}
              {(agency.brands || []).length > 0 && (
                <Box>
                  <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.78rem', color: colors.charcoal, mb: 0.5 }}>
                    Marques ({agency.brands.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {agency.brands.map((b) => (
                      <Chip
                        key={b.id}
                        label={b.name}
                        size="small"
                        sx={{
                          fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', fontWeight: 500,
                          bgcolor: colors.primaryBg, color: colors.primary, borderRadius: '100px',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════
function SuperAdminPage() {
  const { onLogout } = useOutletContext() || {};
  const [tab, setTab] = useState(0);
  const [authorized, setAuthorized] = useState(null);
  const [error, setError] = useState(null);

  // Data
  const [agencies, setAgencies] = useState([]);
  const [, setMetrics] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [aiDays, setAiDays] = useState(30);

  // Loading
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [loadingAi, setLoadingAi] = useState(false);

  // Cockpit
  const [cockpitData, setCockpitData] = useState(null);
  const [loadingCockpit, setLoadingCockpit] = useState(false);

  // Dialogs
  const [tempPassword, setTempPassword] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [createUserAgencyId, setCreateUserAgencyId] = useState(null);
  const [newUserForm, setNewUserForm] = useState({ email: '', full_name: '', role: 'viewer' });
  const [createdUserResult, setCreatedUserResult] = useState(null);

  // Create agency dialog
  const [createAgencyDialogOpen, setCreateAgencyDialogOpen] = useState(false);
  const [newAgencyForm, setNewAgencyForm] = useState({
    name: '', plan: 'production', max_brands: 10,
    contact_email: '', contact_phone: '',
    admin_email: '', admin_full_name: '',
  });
  const [createdAgencyResult, setCreatedAgencyResult] = useState(null);

  // Edit agency dialog
  const [editAgencyDialogOpen, setEditAgencyDialogOpen] = useState(false);
  const [editAgencyForm, setEditAgencyForm] = useState({});
  const [editAgencyId, setEditAgencyId] = useState(null);

  // Pricing (Tarification tab)
  const PRICING_DEFAULTS = {
    base: 500, per_mission: 200, page_comprendre: 150,
    page_agir: 150, freq_hebdo: 300, freq_quotidien: 800, objectifs_etendus: 300,
  };
  const [pricing, setPricing] = useState(PRICING_DEFAULTS);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [pricingSaved, setPricingSaved] = useState(false);

  // ─── Activite (audit logs) ──────────────────────────────────
  const [auditLogs, setAuditLogs] = useState(null);
  const [auditSummary, setAuditSummary] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditDays, setAuditDays] = useState(30);

  // ─── Auth check + load metrics on mount ──────────────────────
  useEffect(() => {
    SUPERADMIN_API.getMetrics()
      .then((res) => {
        setAuthorized(true);
        setMetrics(res.data);
      })
      .catch((err) => {
        if (err.response?.status === 403 || err.response?.status === 401) {
          setAuthorized(false);
        } else {
          setError('Erreur de connexion au serveur');
          setAuthorized(false);
        }
      });
  }, []);

  // ─── Data fetchers ──────────────────────────────────────────
  const fetchAgencies = useCallback(async () => {
    setLoadingAgencies(true);
    try {
      const res = await SUPERADMIN_API.getAgencies();
      setAgencies(res.data || []);
    } catch (err) {
      console.error('Fetch agencies error:', err);
    } finally {
      setLoadingAgencies(false);
    }
  }, []);

  const fetchAiUsage = useCallback(async (days) => {
    setLoadingAi(true);
    try {
      const res = await SUPERADMIN_API.getAiUsage(days);
      setAiUsage(res.data);
    } catch (err) {
      console.error('Fetch AI usage error:', err);
    } finally {
      setLoadingAi(false);
    }
  }, []);

  const fetchPricing = useCallback(async () => {
    setLoadingPricing(true);
    try {
      const res = await SUPERADMIN_API.getPricing();
      const data = res.data || res;
      if (data && typeof data === 'object') {
        setPricing((prev) => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Fetch pricing error:', err);
    } finally {
      setLoadingPricing(false);
    }
  }, []);

  const fetchCockpit = useCallback(async () => {
    setLoadingCockpit(true);
    try {
      const res = await SUPERADMIN_API.getCockpit();
      setCockpitData(res.data || res);
    } catch (err) {
      console.error('Fetch cockpit error:', err);
    } finally {
      setLoadingCockpit(false);
    }
  }, []);

  const fetchAuditLogs = useCallback(async (days) => {
    setLoadingAudit(true);
    try {
      const res = await SUPERADMIN_API.getAuditLogs(days);
      const data = res.data || res;
      setAuditLogs(data.logs || []);
      setAuditSummary(data.summary || null);
    } catch (err) {
      console.error('Fetch audit logs error:', err);
    } finally {
      setLoadingAudit(false);
    }
  }, []);

  // Load data on tab/auth change
  useEffect(() => {
    if (!authorized) return;
    if (tab === 0) fetchCockpit();
    if (tab === 1 && agencies.length === 0) fetchAgencies();
    if (tab === 2) fetchAiUsage(aiDays);
    if (tab === 3) fetchPricing();
    if (tab === 4) fetchAuditLogs(auditDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, authorized]);

  // ─── Handlers ───────────────────────────────────────────────
  const handleToggleAgency = async (agency) => {
    try {
      await SUPERADMIN_API.updateAgency(agency.id, { is_active: !agency.is_active });
      setAgencies((prev) =>
        prev.map((a) => (a.id === agency.id ? { ...a, is_active: !a.is_active } : a))
      );
    } catch (err) {
      console.error('Toggle agency error:', err);
    }
  };

  const handleToggleUser = async (user, field) => {
    const update = {};
    if (field === 'active') update.is_active = !user.is_active;
    try {
      await SUPERADMIN_API.updateUser(user.id, update);
      // Update the user inside the agency
      setAgencies((prev) =>
        prev.map((a) => ({
          ...a,
          users: (a.users || []).map((u) =>
            u.id === user.id ? { ...u, ...update } : u
          ),
        }))
      );
    } catch (err) {
      console.error('Toggle user error:', err);
    }
  };

  const handleToggleSuperadmin = async (user) => {
    try {
      await SUPERADMIN_API.updateUser(user.id, { is_superadmin: !user.is_superadmin });
      setAgencies((prev) =>
        prev.map((a) => ({
          ...a,
          users: (a.users || []).map((u) =>
            u.id === user.id ? { ...u, is_superadmin: !u.is_superadmin } : u
          ),
        }))
      );
    } catch (err) {
      console.error('Toggle superadmin error:', err);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await SUPERADMIN_API.resetPassword(userId);
      setTempPassword(res.data?.temporary_password || res.data?.temp_password || 'Voir email');
      setPasswordDialogOpen(true);
    } catch (err) {
      console.error('Reset password error:', err);
    }
  };

  const handleOpenCreateUser = (agencyId) => {
    setCreateUserAgencyId(agencyId);
    setNewUserForm({ email: '', full_name: '', role: 'viewer' });
    setCreatedUserResult(null);
    setCreateUserDialogOpen(true);
  };

  const handleCreateUser = async () => {
    try {
      const res = await SUPERADMIN_API.createUser(createUserAgencyId, newUserForm);
      setCreatedUserResult(res.data);
      // Refresh agencies to get updated user list
      fetchAgencies();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors de la creation';
      setCreatedUserResult({ error: detail });
    }
  };

  const handleCreateAgency = async () => {
    try {
      const res = await SUPERADMIN_API.createAgency(newAgencyForm);
      setCreatedAgencyResult(res.data);
      fetchAgencies();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors de la creation';
      setCreatedAgencyResult({ error: detail });
    }
  };

  const handleOpenEditAgency = (agency) => {
    setEditAgencyId(agency.id);
    setEditAgencyForm({
      monthly_billing: agency.monthly_billing || 0,
      contact_email: agency.contact_email || '',
      contact_phone: agency.contact_phone || '',
    });
    setEditAgencyDialogOpen(true);
  };

  const handleSaveEditAgency = async () => {
    try {
      await SUPERADMIN_API.updateAgency(editAgencyId, editAgencyForm);
      setEditAgencyDialogOpen(false);
      fetchAgencies();
    } catch (err) {
      console.error('Update agency error:', err);
    }
  };

  const handleAiDaysChange = (days) => {
    setAiDays(days);
    fetchAiUsage(days);
  };

  const handleSavePricing = async () => {
    try {
      await SUPERADMIN_API.updatePricing(pricing);
      setPricingSaved(true);
    } catch (err) {
      console.error('Update pricing error:', err);
    }
  };

  // ─── Render gate ────────────────────────────────────────────
  if (authorized === null) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 80px)', background: `linear-gradient(180deg, ${colors.ivory} 0%, ${colors.ivoryWarm} 100%)` }}>
        <CircularProgress sx={{ color: colors.primary, mb: 2 }} />
        <Typography sx={{ fontFamily: 'DM Sans, sans-serif', color: colors.textMed }}>
          Verification des droits...
        </Typography>
      </Box>
    );
  }

  if (!authorized) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 80px)', background: `linear-gradient(180deg, ${colors.ivory} 0%, ${colors.ivoryWarm} 100%)`, px: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 500, borderRadius: '16px', fontFamily: 'DM Sans, sans-serif' }}>
          {error || "Acces refuse. Vous n'etes pas superadmin."}
        </Alert>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // Main render
  // ═══════════════════════════════════════════════════════════
  return (
    <Box
      sx={{
        background: `linear-gradient(180deg, ${colors.ivory} 0%, ${colors.ivoryWarm} 100%)`,
        minHeight: 'calc(100vh - 80px)',
        pb: 6,
        maxWidth: 1400,
        mx: 'auto',
        px: { xs: 2, sm: 4, md: 5 },
      }}
    >
      {/* ── Sticky Header Bar (FloatingNav-style) ────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          mb: 3,
          mt: 2,
          borderRadius: { xs: '20px', md: '100px' },
          background: colors.cardGlass,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${colors.borderLight}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          gap: { xs: 0.5, md: 0 },
        }}
      >
        {/* Top row on mobile: logo + logout */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo Chutt... */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, pl: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: colors.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 2px 8px ${colors.primaryBg}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 18 }}>
                <Box sx={{ width: 3.5, height: '100%', borderRadius: '2px', bgcolor: colors.white }} />
                <Box sx={{ width: 3.5, height: '72%', borderRadius: '2px', bgcolor: colors.white, opacity: 0.8 }} />
                <Box sx={{ width: 3.5, height: '44%', borderRadius: '2px', bgcolor: colors.white, opacity: 0.5 }} />
              </Box>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Dancing Script", cursive',
                fontSize: '1.4rem',
                fontWeight: 700,
                color: colors.primary,
                lineHeight: 1,
              }}
            >
              Chutt...
            </Typography>
          </Box>

          {/* Logout visible on mobile in top row, hidden on desktop (shown separately) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            {onLogout && (
              <IconButton
                size="small"
                aria-label="Deconnexion"
                onClick={onLogout}
                sx={{ color: colors.textLight, '&:hover': { color: colors.danger } }}
              >
                <LogoutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Tabs (scrollable on mobile) */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            '& .MuiTabs-scrollButtons': {
              color: colors.textMed,
              '&.Mui-disabled': { opacity: 0.3 },
            },
            '& .MuiTab-root': {
              fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.8rem',
              textTransform: 'none', color: colors.textMed, letterSpacing: '0.02em',
              minHeight: 36, py: 0, px: { xs: 1.5, md: 2 }, zIndex: 1,
              minWidth: 'auto',
              '&.Mui-selected': { color: colors.white },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colors.primary, height: '100%', borderRadius: '100px', zIndex: 0,
            },
          }}
        >
          <Tab label="Cockpit" />
          <Tab label="Clients" />
          <Tab label="Couts API" />
          <Tab label="Tarification" />
          <Tab label="Activite" />
        </Tabs>

        {/* Deconnexion (desktop only, mobile is in top row) */}
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          {onLogout && (
            <Tooltip title="Deconnexion">
              <IconButton
                size="small"
                aria-label="Deconnexion"
                onClick={onLogout}
                sx={{ color: colors.textLight, flexShrink: 0, '&:hover': { color: colors.danger } }}
              >
                <LogoutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════ */}
      {/* TAB 0 — COCKPIT                                    */}
      {/* ═══════════════════════════════════════════════════ */}
      <TabPanel value={tab} index={0}>
        {/* Page title */}
        <Box sx={{ pt: 5, pb: 4, mb: 4 }}>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: '3.2rem', sm: '4rem', md: '4.8rem' },
              fontWeight: 400,
              color: colors.charcoal,
              lineHeight: 1.1,
            }}
          >
            Cockpit <em style={{ color: colors.primary, fontStyle: 'italic' }}>Superadmin</em>
          </Typography>
          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '1rem',
              color: colors.textMed,
              mt: 1,
            }}
          >
            Vue consolidee de la plateforme — {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </Typography>
        </Box>

        {loadingCockpit && !cockpitData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : cockpitData ? (
          <Box>
            <ScrollReveal>
              <SAKPIGrid kpis={cockpitData.kpis} agencies={cockpitData.agencies} />
            </ScrollReveal>
            <ScrollReveal>
              <SAAgencyList agencies={cockpitData.agencies} />
            </ScrollReveal>
            <Box sx={{ mt: 3 }}>
              <ScrollReveal>
                <SACharts charts={cockpitData.charts} agencies={cockpitData.agencies} />
              </ScrollReveal>
            </Box>
            <ScrollReveal>
              <SAFooterCards footer={cockpitData.footer} />
            </ScrollReveal>
          </Box>
        ) : null}
      </TabPanel>

      {/* ═══════════════════════════════════════════════════ */}
      {/* TAB 1 — CLIENTS (agencies + expandable users)      */}
      {/* ═══════════════════════════════════════════════════ */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ pt: 5, pb: 4, mb: 4 }}>
          <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: { xs: '3.2rem', sm: '4rem', md: '4.8rem' }, fontWeight: 400, color: colors.charcoal, lineHeight: 1.1 }}>
            Clients <em style={{ color: colors.primary, fontStyle: 'italic' }}>Superadmin</em>
          </Typography>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '1rem', color: colors.textMed, mt: 1 }}>
            Gestion des agences et utilisateurs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewAgencyForm({ name: '', plan: 'production', max_brands: 10, contact_email: '', contact_phone: '', admin_email: '', admin_full_name: '' });
              setCreatedAgencyResult(null);
              setCreateAgencyDialogOpen(true);
            }}
            sx={{
              borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', textTransform: 'none',
              bgcolor: colors.primary, color: colors.white,
              '&:hover': { bgcolor: colors.primaryDark },
            }}
          >
            Nouvelle agence
          </Button>
        </Box>

        {loadingAgencies ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : (
          <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx} width={30} />
                    <TableCell sx={headCellSx}>Nom</TableCell>
                    <TableCell sx={headCellSx}>Plan</TableCell>
                    <TableCell sx={headCellSx} align="center">Marques</TableCell>
                    <TableCell sx={headCellSx} align="center">Users</TableCell>
                    <TableCell sx={headCellSx} align="center">Statut</TableCell>
                    <TableCell sx={headCellSx} align="right">Facturation/mois</TableCell>
                    <TableCell sx={headCellSx}>Dern. facture</TableCell>
                    <TableCell sx={headCellSx}>Contact</TableCell>
                    <TableCell sx={headCellSx} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agencies.map((agency) => (
                    <AgencyRow
                      key={agency.id}
                      agency={agency}
                      onToggleAgency={handleToggleAgency}
                      onToggleUser={handleToggleUser}
                      onResetPassword={handleResetPassword}
                      onToggleSuperadmin={handleToggleSuperadmin}
                      onCreateUser={handleOpenCreateUser}
                      onEditAgency={handleOpenEditAgency}
                    />
                  ))}
                  {agencies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ ...bodyCellSx, textAlign: 'center', py: 4, color: colors.textLight }}>
                        Aucune agence trouvee
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </GlassCard>
        )}
      </TabPanel>

      {/* ═══════════════════════════════════════════════════ */}
      {/* TAB 2 — COUTS API (grouped by provider)            */}
      {/* ═══════════════════════════════════════════════════ */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ pt: 5, pb: 4, mb: 4 }}>
          <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: { xs: '3.2rem', sm: '4rem', md: '4.8rem' }, fontWeight: 400, color: colors.charcoal, lineHeight: 1.1 }}>
            Couts <em style={{ color: colors.primary, fontStyle: 'italic' }}>API</em>
          </Typography>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '1rem', color: colors.textMed, mt: 1 }}>
            Suivi des consommations IA par provider
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              variant={aiDays === d ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleAiDaysChange(d)}
              sx={{
                borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', textTransform: 'none', fontSize: '0.8rem',
                ...(aiDays === d
                  ? { bgcolor: colors.primary, color: colors.white, '&:hover': { bgcolor: colors.primaryDark } }
                  : { borderColor: colors.border, color: colors.textMed, '&:hover': { borderColor: colors.primary, color: colors.primary, bgcolor: colors.primaryBg } }),
              }}
            >
              {d}j
            </Button>
          ))}
        </Box>

        {loadingAi ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : aiUsage?.per_provider && aiUsage.per_provider.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Provider summary cards — single row grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${Math.min(aiUsage.per_provider.length, 4)}, 1fr)` }, gap: 2 }}>
              {aiUsage.per_provider.map((prov) => {
                const meta = getProviderMeta(prov.provider);
                return (
                  <GlassCard key={prov.provider} sx={{ p: 2.5 }}>
                    <Chip
                      label={meta.label}
                      size="small"
                      sx={{
                        fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600,
                        bgcolor: meta.bg, color: meta.color, borderRadius: '100px', mb: 1.5,
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box>
                        <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', color: colors.textMed }}>
                          Appels
                        </Typography>
                        <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: colors.charcoal }}>
                          {Number(prov.call_count).toLocaleString('fr-FR')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', color: colors.textMed }}>
                          Cout
                        </Typography>
                        <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: colors.charcoal }}>
                          {Number(prov.total_cost).toLocaleString('fr-FR', { style: 'currency', currency: 'USD' })}
                        </Typography>
                      </Box>
                      {prov.total_tokens > 0 && (
                        <Box>
                          <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', color: colors.textMed }}>
                            Tokens
                          </Typography>
                          <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: colors.charcoal }}>
                            {Number(prov.total_tokens).toLocaleString('fr-FR')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </GlassCard>
                );
              })}
            </Box>

            {/* Total row */}
            <GlassCard sx={{ p: 2.5, borderLeft: `3px solid ${colors.primary}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography sx={{ fontFamily: 'Playfair Display, serif', fontSize: '1rem', fontWeight: 700, color: colors.charcoal }}>
                  Total ({aiUsage.days}j)
                </Typography>
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Box>
                    <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', color: colors.textMed }}>
                      Appels
                    </Typography>
                    <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.2rem', fontWeight: 700, color: colors.charcoal }}>
                      {Number(aiUsage.per_provider.reduce((s, r) => s + r.call_count, 0)).toLocaleString('fr-FR')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', color: colors.textMed }}>
                      Cout total
                    </Typography>
                    <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.2rem', fontWeight: 700, color: colors.primary }}>
                      {Number(aiUsage.per_provider.reduce((s, r) => s + r.total_cost, 0)).toLocaleString('fr-FR', { style: 'currency', currency: 'USD' })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </GlassCard>

            {/* Detail table per endpoint */}
            <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: colors.charcoal }}>
                  Detail par service
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Provider</TableCell>
                      <TableCell sx={headCellSx}>Modele</TableCell>
                      <TableCell sx={headCellSx}>Endpoint</TableCell>
                      <TableCell sx={headCellSx} align="right">Appels</TableCell>
                      <TableCell sx={headCellSx} align="right">Tokens</TableCell>
                      <TableCell sx={headCellSx} align="right">Cout</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(aiUsage.per_model || []).map((row, idx) => {
                      const meta = getProviderMeta(row.provider);
                      return (
                        <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: colors.emeraldGlass } }}>
                          <TableCell sx={bodyCellSx}>
                            <Chip
                              label={meta.label}
                              size="small"
                              sx={{
                                fontFamily: 'DM Sans, sans-serif', fontSize: '0.65rem', fontWeight: 600,
                                bgcolor: meta.bg, color: meta.color, borderRadius: '100px',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem' }}>
                              {row.model}
                            </Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: colors.textMed }}>
                              {row.endpoint}
                            </Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {Number(row.call_count).toLocaleString('fr-FR')}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {Number(row.total_tokens).toLocaleString('fr-FR')}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {Number(row.total_cost).toLocaleString('fr-FR', { style: 'currency', currency: 'USD' })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </GlassCard>
          </Box>
        ) : (
          <Alert severity="info" sx={{ borderRadius: '16px', fontFamily: 'DM Sans, sans-serif' }}>
            Aucune donnee de couts API pour cette periode
          </Alert>
        )}
      </TabPanel>

      {/* ═══════════════════════════════════════════════════ */}
      {/* TAB 3 — TARIFICATION                                */}
      {/* ═══════════════════════════════════════════════════ */}
      <TabPanel value={tab} index={3}>
        <Box sx={{ pt: 5, pb: 4, mb: 4 }}>
          <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: { xs: '3.2rem', sm: '4rem', md: '4.8rem' }, fontWeight: 400, color: colors.charcoal, lineHeight: 1.1 }}>
            Tarification <em style={{ color: colors.primary, fontStyle: 'italic' }}>Client</em>
          </Typography>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '1rem', color: colors.textMed, mt: 1 }}>
            Configuration de la grille tarifaire
          </Typography>
        </Box>
        {loadingPricing ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : (
          <GlassCard sx={{ p: { xs: 2.5, md: 4 } }}>
            <Typography
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontSize: '1.15rem',
                fontWeight: 600,
                color: colors.charcoal,
                mb: 3,
              }}
            >
              Grille tarifaire
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2.5,
                mb: 4,
              }}
            >
              {[
                { key: 'base', label: 'Base (1 mission + Radar + Mesurer + mensuel)' },
                { key: 'per_mission', label: 'Par mission supplementaire' },
                { key: 'page_comprendre', label: 'Page Comprendre' },
                { key: 'page_agir', label: 'Page Agir' },
                { key: 'freq_hebdo', label: 'Frequence hebdomadaire' },
                { key: 'freq_quotidien', label: 'Frequence quotidienne' },
                { key: 'objectifs_etendus', label: 'Objectifs etendus' },
              ].map(({ key, label }) => (
                <TextField
                  key={key}
                  label={label}
                  type="number"
                  value={pricing[key] ?? ''}
                  onChange={(e) => setPricing((prev) => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: colors.textMed }}>
                          &euro;
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'DM Sans, sans-serif',
                      borderRadius: '12px',
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '0.85rem',
                    },
                  }}
                />
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePricing}
                sx={{
                  borderRadius: '100px',
                  fontFamily: 'DM Sans, sans-serif',
                  textTransform: 'none',
                  bgcolor: colors.primary,
                  px: 4,
                  '&:hover': { bgcolor: colors.primaryDark },
                }}
              >
                Mettre a jour la grille tarifaire
              </Button>
            </Box>
          </GlassCard>
        )}
      </TabPanel>

      {/* ═══════════════════════════════════════════════════ */}
      {/* TAB 4 — ACTIVITE                                   */}
      {/* ═══════════════════════════════════════════════════ */}
      <TabPanel value={tab} index={4}>
        <Box sx={{ pt: 5, pb: 4, mb: 4 }}>
          <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: { xs: '3.2rem', sm: '4rem', md: '4.8rem' }, fontWeight: 400, color: colors.charcoal, lineHeight: 1.1 }}>

            Journal d'<em style={{ color: colors.primary, fontStyle: 'italic' }}>Activite</em>
          </Typography>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '1rem', color: colors.textMed, mt: 1 }}>
            Connexions et actions des utilisateurs de la plateforme
          </Typography>
        </Box>

        {/* Summary cards */}
        {auditSummary && (
          <ScrollReveal>
            <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
              {[
                { label: 'Evenements', value: auditSummary.total_events || 0 },
                { label: 'Utilisateurs actifs (7j)', value: auditSummary.active_users_7d || 0 },
                { label: 'Connexions', value: auditSummary.actions_breakdown?.['user.login'] || 0 },
              ].map((kpi) => (
                <GlassCard key={kpi.label} sx={{ flex: '1 1 200px', minWidth: 160, p: 2.5, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '2rem', fontWeight: 600, color: colors.charcoal }}>
                    {kpi.value}
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', color: colors.textMed, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {kpi.label}
                  </Typography>
                </GlassCard>
              ))}
            </Box>
          </ScrollReveal>
        )}

        {/* Period selector */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              variant={auditDays === d ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setAuditDays(d); fetchAuditLogs(d); }}
              sx={{
                borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', textTransform: 'none', fontSize: '0.8rem',
                ...(auditDays === d
                  ? { bgcolor: colors.primary, color: colors.white, '&:hover': { bgcolor: colors.primaryDark } }
                  : { borderColor: colors.border, color: colors.textMed, '&:hover': { borderColor: colors.primary, color: colors.primary, bgcolor: colors.primaryBg } }),
              }}
            >
              {d}j
            </Button>
          ))}
        </Box>

        {loadingAudit ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : auditLogs && auditLogs.length > 0 ? (
          <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: `${colors.primary}08` }}>
                    {['Date', 'Action', 'Utilisateur', 'Agence', 'Details'].map((h) => (
                      <TableCell key={h} sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMed, borderBottom: `2px solid ${colors.primary}20`, py: 1.5 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLogs.map((log) => {
                    const ACTION_LABELS = {
                      'user.login': { label: 'Connexion', color: colors.info },
                      'user.register': { label: 'Inscription', color: colors.success },
                      'user.create': { label: 'Creation utilisateur', color: colors.success },
                      'user.update': { label: 'Modification utilisateur', color: colors.warning },
                      'user.reset_password': { label: 'Reset mot de passe', color: colors.warning },
                      'agency.create': { label: 'Creation agence', color: colors.success },
                      'agency.update': { label: 'Modification agence', color: colors.warning },
                      'brand.create': { label: 'Creation marque', color: colors.success },
                      'brand.update': { label: 'Modification marque', color: colors.info },
                      'brand.delete': { label: 'Suppression marque', color: colors.danger },
                      'brand_plan.update': { label: 'Modification forfait', color: colors.warning },
                      'pricing.update': { label: 'Modification tarification', color: colors.warning },
                    };
                    const actionInfo = ACTION_LABELS[log.action] || { label: log.action, color: colors.textMed };
                    const dateStr = log.created_at
                      ? new Date(log.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '--';
                    const detailStr = log.details
                      ? (typeof log.details === 'object'
                        ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')
                        : String(log.details))
                      : '';

                    return (
                      <TableRow key={log.id} sx={{ '&:hover': { bgcolor: `${colors.primary}04` } }}>
                        <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.textMed, whiteSpace: 'nowrap', py: 1.2 }}>
                          {dateStr}
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <Chip
                            label={actionInfo.label}
                            size="small"
                            sx={{
                              bgcolor: `${actionInfo.color}18`,
                              color: actionInfo.color,
                              fontFamily: '"DM Sans", sans-serif',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22,
                              borderRadius: '6px',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal, py: 1.2 }}>
                          {log.user?.email || '--'}
                        </TableCell>
                        <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.textMed, py: 1.2 }}>
                          {log.agency || '--'}
                        </TableCell>
                        <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textLight, py: 1.2, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {detailStr || '--'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </GlassCard>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', color: colors.textLight, fontSize: '0.9rem' }}>
              Aucun evenement enregistre sur cette periode.
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Success snackbar */}
      <Snackbar
        open={pricingSaved}
        autoHideDuration={3000}
        onClose={() => setPricingSaved(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setPricingSaved(false)}
          sx={{ borderRadius: '12px', fontFamily: 'DM Sans, sans-serif' }}
        >
          Grille tarifaire mise a jour avec succes
        </Alert>
      </Snackbar>

      {/* ═══════════════════════════════════════════════════ */}
      {/* Temp password dialog                               */}
      {/* ═══════════════════════════════════════════════════ */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '16px', background: colors.ivory } }}
      >
        <DialogTitle sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, color: colors.charcoal }}>
          Mot de passe temporaire
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ borderRadius: '12px', fontFamily: 'DM Sans, sans-serif', mb: 2 }}>
            Le mot de passe a ete reinitialise avec succes.
          </Alert>
          <TextField
            fullWidth
            label="Mot de passe temporaire"
            value={tempPassword || ''}
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiOutlinedInput-root': { fontFamily: 'DM Sans, sans-serif', borderRadius: '12px', bgcolor: colors.gray100 },
              '& .MuiInputLabel-root': { fontFamily: 'DM Sans, sans-serif' },
            }}
          />
          <Typography sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: colors.textLight, mt: 1 }}>
            Communiquez ce mot de passe de maniere securisee.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setPasswordDialogOpen(false)}
            sx={{ borderRadius: '100px', fontFamily: 'DM Sans, sans-serif', textTransform: 'none', bgcolor: colors.primary, '&:hover': { bgcolor: colors.primaryDark } }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════ */}
      {/* Create agency dialog                               */}
      {/* ═══════════════════════════════════════════════════ */}
      <Dialog
        open={createAgencyDialogOpen}
        onClose={() => setCreateAgencyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', background: colors.ivory } }}
      >
        <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: colors.charcoal }}>
          Nouvelle agence
        </DialogTitle>
        <DialogContent>
          {createdAgencyResult && !createdAgencyResult.error ? (
            <Box>
              <Alert severity="success" sx={{ borderRadius: '12px', fontFamily: '"DM Sans", sans-serif', mb: 2 }}>
                Agence creee avec succes !
              </Alert>
              <TextField
                fullWidth label="Nom de l'agence" value={createdAgencyResult.agency?.name || ''} InputProps={{ readOnly: true }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontFamily: '"DM Sans", sans-serif', borderRadius: '12px', bgcolor: colors.gray100 }, '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif' } }}
              />
              <TextField
                fullWidth label="Email admin" value={createdAgencyResult.admin?.email || ''} InputProps={{ readOnly: true }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontFamily: '"DM Sans", sans-serif', borderRadius: '12px', bgcolor: colors.gray100 }, '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif' } }}
              />
              <TextField
                fullWidth label="Mot de passe temporaire" value={createdAgencyResult.admin?.temporary_password || ''} InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"DM Sans", sans-serif', borderRadius: '12px', bgcolor: colors.gray100 }, '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif' } }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {createdAgencyResult?.error && (
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: '"DM Sans", sans-serif' }}>
                  {createdAgencyResult.error}
                </Alert>
              )}
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', fontWeight: 600, color: colors.textMed, textTransform: 'uppercase', letterSpacing: '0.08em', mt: 1 }}>
                Informations agence
              </Typography>
              <TextField
                fullWidth label="Nom de l'agence" value={newAgencyForm.name}
                onChange={(e) => setNewAgencyForm({ ...newAgencyForm, name: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"DM Sans", sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif' } }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontFamily: '"DM Sans", sans-serif' }}>Plan</InputLabel>
                  <Select
                    value={newAgencyForm.plan} label="Plan"
                    onChange={(e) => setNewAgencyForm({ ...newAgencyForm, plan: e.target.value })}
                    sx={{ fontFamily: '"DM Sans", sans-serif', borderRadius: '12px' }}
                  >
                    <MenuItem value="demonstration">Demonstration (0 &euro;/mois)</MenuItem>
                    <MenuItem value="production">Production (3 000 &euro;/mois)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth label="Max marques" type="number" value={newAgencyForm.max_brands}
                  onChange={(e) => setNewAgencyForm({ ...newAgencyForm, max_brands: parseInt(e.target.value) || 10 })}
                  sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"DM Sans", sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif' } }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label="Email de contact" value={newAgencyForm.contact_email}
                  onChange={(e) => setNewAgencyForm({ ...newAgencyForm, contact_email: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"DM Sans", sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif' } }}
                />
                <TextField
                  fullWidth label="Telephone" value={newAgencyForm.contact_phone}
                  onChange={(e) => setNewAgencyForm({ ...newAgencyForm, contact_phone: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"DM Sans", sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif' } }}
                />
              </Box>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', fontWeight: 600, color: colors.textMed, textTransform: 'uppercase', letterSpacing: '0.08em', mt: 1 }}>
                Administrateur de l'agence
              </Typography>
              <TextField
                fullWidth label="Nom complet de l'admin" value={newAgencyForm.admin_full_name}
                onChange={(e) => setNewAgencyForm({ ...newAgencyForm, admin_full_name: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"DM Sans", sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif' } }}
              />
              <TextField
                fullWidth label="Email de l'admin" value={newAgencyForm.admin_email}
                onChange={(e) => setNewAgencyForm({ ...newAgencyForm, admin_email: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"DM Sans", sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif' } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateAgencyDialogOpen(false)}
            sx={{ borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', textTransform: 'none', color: colors.textMed }}
          >
            Fermer
          </Button>
          {!createdAgencyResult?.agency && (
            <Button
              variant="contained"
              onClick={handleCreateAgency}
              disabled={!newAgencyForm.name || !newAgencyForm.admin_email || !newAgencyForm.admin_full_name}
              sx={{ borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', textTransform: 'none', bgcolor: colors.primary, '&:hover': { bgcolor: colors.primaryDark } }}
            >
              Creer l'agence
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════ */}
      {/* Create user dialog                                 */}
      {/* ═══════════════════════════════════════════════════ */}
      <Dialog
        open={createUserDialogOpen}
        onClose={() => setCreateUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', background: colors.ivory } }}
      >
        <DialogTitle sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, color: colors.charcoal }}>
          Creer un utilisateur
        </DialogTitle>
        <DialogContent>
          {createdUserResult && !createdUserResult.error ? (
            <Box>
              <Alert severity="success" sx={{ borderRadius: '12px', fontFamily: 'DM Sans, sans-serif', mb: 2 }}>
                Utilisateur cree avec succes !
              </Alert>
              <TextField
                fullWidth label="Email" value={createdUserResult.email || ''} InputProps={{ readOnly: true }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontFamily: 'DM Sans, sans-serif', borderRadius: '12px', bgcolor: colors.gray100 }, '& .MuiInputLabel-root': { fontFamily: 'DM Sans, sans-serif' } }}
              />
              <TextField
                fullWidth label="Mot de passe temporaire" value={createdUserResult.temporary_password || ''} InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'DM Sans, sans-serif', borderRadius: '12px', bgcolor: colors.gray100 }, '& .MuiInputLabel-root': { fontFamily: 'DM Sans, sans-serif' } }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {createdUserResult?.error && (
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'DM Sans, sans-serif' }}>
                  {createdUserResult.error}
                </Alert>
              )}
              <TextField
                fullWidth label="Email" value={newUserForm.email}
                onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'DM Sans, sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: 'DM Sans, sans-serif' } }}
              />
              <TextField
                fullWidth label="Nom complet" value={newUserForm.full_name}
                onChange={(e) => setNewUserForm({ ...newUserForm, full_name: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'DM Sans, sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: 'DM Sans, sans-serif' } }}
              />
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily: 'DM Sans, sans-serif' }}>Role</InputLabel>
                <Select
                  value={newUserForm.role}
                  label="Role"
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                  sx={{ fontFamily: 'DM Sans, sans-serif', borderRadius: '12px' }}
                >
                  <MenuItem value="viewer">Viewer</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin_agence">Admin Agence</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateUserDialogOpen(false)}
            sx={{ borderRadius: '100px', fontFamily: 'DM Sans, sans-serif', textTransform: 'none', color: colors.textMed }}
          >
            Fermer
          </Button>
          {!createdUserResult?.email && (
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={!newUserForm.email || !newUserForm.full_name}
              sx={{ borderRadius: '100px', fontFamily: 'DM Sans, sans-serif', textTransform: 'none', bgcolor: colors.primary, '&:hover': { bgcolor: colors.primaryDark } }}
            >
              Creer
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════ */}
      {/* Edit agency dialog                                 */}
      {/* ═══════════════════════════════════════════════════ */}
      <Dialog
        open={editAgencyDialogOpen}
        onClose={() => setEditAgencyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', background: colors.ivory } }}
      >
        <DialogTitle sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, color: colors.charcoal }}>
          Editer agence
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth label="Facturation mensuelle (EUR)" type="number"
              value={editAgencyForm.monthly_billing || ''}
              onChange={(e) => setEditAgencyForm({ ...editAgencyForm, monthly_billing: parseFloat(e.target.value) || 0 })}
              sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'DM Sans, sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: 'DM Sans, sans-serif' } }}
            />
            <TextField
              fullWidth label="Email de contact"
              value={editAgencyForm.contact_email || ''}
              onChange={(e) => setEditAgencyForm({ ...editAgencyForm, contact_email: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'DM Sans, sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: 'DM Sans, sans-serif' } }}
            />
            <TextField
              fullWidth label="Telephone de contact"
              value={editAgencyForm.contact_phone || ''}
              onChange={(e) => setEditAgencyForm({ ...editAgencyForm, contact_phone: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'DM Sans, sans-serif', borderRadius: '12px' }, '& .MuiInputLabel-root': { fontFamily: 'DM Sans, sans-serif' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditAgencyDialogOpen(false)}
            sx={{ borderRadius: '100px', fontFamily: 'DM Sans, sans-serif', textTransform: 'none', color: colors.textMed }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEditAgency}
            sx={{ borderRadius: '100px', fontFamily: 'DM Sans, sans-serif', textTransform: 'none', bgcolor: colors.primary, '&:hover': { bgcolor: colors.primaryDark } }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SuperAdminPage;
